<template>
  <div class="create-post-container">
    <h2>Creează o Postare Nouă</h2>
    <p>
      Folosește acest formular pentru a adăuga postări noi. Poți trage (drag &
      drop) imaginile pentru a le reordona.
    </p>

    <form @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="description">Descriere:</label>
        <textarea
          id="description"
          v-model="post.description"
          rows="5"
          required
          placeholder="Adaugă descrierea postării..."
        ></textarea>
      </div>

      <div class="form-group">
        <label>Imagini (max 10):</label>

        <div
          ref="dropZoneRef"
          :class="['dropzone', { 'drag-over': isOverDropZone }]"
          @click="triggerFileInput"
        >
          <p>
            Trage fișierele aici sau dă click pentru a le selecta.
          </p>
        </div>

        <input
          type="file"
          id="images"
          @change="handleFileChange"
          accept="image/*"
          multiple
          ref="fileInputRef"
          style="display: none"
        />

        <p v-if="files.length > 0" class="preview-label">
          Previzualizare (ține apăsat și trage pentru a reordona):
        </p>
        <draggable
          v-model="files"
          item-key="id"
          class="preview-container"
          animation="200"
        >
          <template #item="{ element: file }">
            <div class="thumbnail">
              <img :src="file.preview" :alt="file.file.name" />
              <button
                type="button"
                @click="removeFile(file.id)"
                class="remove-btn"
              >
                &times;
              </button>
            </div>
          </template>
        </draggable>
      </div>

      <div class="form-group">
        <label for="custom_date">Data Postării:</label>
        <input
          type="datetime-local"
          id="custom_date"
          v-model="post.created_at"
          required
        />
      </div>

      <div class="button-group">
        <button type="submit" :disabled="loading || files.length === 0">
          {{ loading ? "Se publică..." : "Publică Postarea" }}
        </button>
      </div>

      <p
        v-if="message"
        :class="{ 'success-message': isSuccess, 'error-message': !isSuccess }"
      >
        {{ message }}
      </p>
    </form>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from "vue";
import apiClient from "@/api.js";
import { useDropZone } from "@vueuse/core"; // Hook-ul pentru Dropzone
import draggable from "vuedraggable"; // Componenta de reordonare

// --- Funcții ajutătoare ---
const getLocalISOTime = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};
const generateId = () => Math.random().toString(36).substr(2, 9);

// --- Starea (State) ---
const post = ref({
  description: "",
  created_at: getLocalISOTime(),
});

// NOU: Starea pentru fișiere. Acum este un array de obiecte.
// Fiecare obiect va fi: { id: '...', file: FileObject, preview: 'blob:...' }
const files = ref([]);

const loading = ref(false);
const message = ref("");
const isSuccess = ref(false);

// Referințe către elementele din DOM
const dropZoneRef = ref(null);
const fileInputRef = ref(null);

// --- Logica de procesare a fișierelor ---
const processFiles = (fileList) => {
  if (files.value.length + fileList.length > 10) {
    alert("Poți încărca maximum 10 imagini în total.");
    return;
  }

  // Iterăm peste fișiere și le adăugăm în starea `files`
  for (const file of fileList) {
    const newFile = {
      id: generateId(), // ID unic pentru reordonare
      file: file, // Obiectul File
      preview: URL.createObjectURL(file), // URL de previzualizare
    };
    files.value.push(newFile);
  }
};

// --- Dropzone (pentru drag-and-drop) ---
function onDrop(droppedFiles) {
  processFiles(droppedFiles);
}
const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);

// --- Input de fișier (pentru click) ---
const handleFileChange = (event) => {
  processFiles(event.target.files);
  // Resetăm input-ul pentru a putea selecta aceleași fișiere din nou
  event.target.value = null;
};

// Funcție pentru a deschide programatic input-ul de fișier
const triggerFileInput = () => {
  fileInputRef.value.click();
};

// --- Funcții utilitare ---
const removeFile = (fileId) => {
  const fileToRemove = files.value.find((f) => f.id === fileId);
  if (fileToRemove) {
    URL.revokeObjectURL(fileToRemove.preview); // Curățăm memoria
  }
  files.value = files.value.filter((f) => f.id !== fileId);
};

const resetForm = () => {
  post.value = {
    description: "",
    created_at: getLocalISOTime(),
  };
  // Curățăm toate URL-urile de previzualizare din memorie
  files.value.forEach((f) => URL.revokeObjectURL(f.preview));
  files.value = [];
  message.value = "";
  isSuccess.value = false;
};

// Curățăm memoria dacă utilizatorul părăsește pagina
onUnmounted(() => {
  files.value.forEach((f) => URL.revokeObjectURL(f.preview));
});

// --- Submit ---
const handleSubmit = async () => {
  console.log('--- PAS 1: handleSubmit APELAT ---');

  if (loading.value) {
    console.error('--- EROARE SILENȚIOASĂ: loading.value este TRUE. Funcția se oprește. ---');
    return; 
  }

  console.log('--- PAS 2: Trecut de garda "loading" ---');

  if (files.value.length === 0) {
    console.error('--- EROARE SILENȚIOASĂ: Nu sunt fișiere. Funcția se oprește. ---');
    message.value = "Te rog adaugă cel puțin o imagine.";
    isSuccess.value = false;
    return;
  }
  
  console.log('--- PAS 3: Se setează loading.value = true ---');
  loading.value = true;
  message.value = "";
  isSuccess.value = false;

  const formData = new FormData();
  formData.append("description", post.value.description);
  const postDate = new Date(post.value.created_at);
  formData.append("created_at", postDate.toISOString());

  for (const fileItem of files.value) {
    formData.append("images", fileItem.file);
  }

  try {
    console.log('--- PAS 4: Se trimite cererea API... ---');
    const response = await apiClient.post("/posts", formData);
    
    console.log('--- PAS 5 (SUCCES): Răspuns primit ---', response.data);
    message.value = response.data.message;
    isSuccess.value = true;
    resetForm();

  } catch (error) {
    console.error('--- PAS 5 (EROARE): Intrat în CATCH ---');
    
    // Logica de eroare pe care am adăugat-o data trecută
    console.error('EROARE COMPLETĂ (DEBUG):', error); 

    if (error.response) {
      console.error('Răspunsul Serverului:', error.response.data);
      console.error('Statusul Serverului:', error.response.status);
      const status = error.response.status;
      const errorData = error.response.data;
      let errorMessage = "A apărut o problemă.";
      if (status === 413) {
        errorMessage = "Imaginile sunt prea mari! Limita serverului a fost depășită.";
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData && errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData && errorData.message) {
        errorMessage = errorData.message;
      }
      message.value = `Eroare ${status}: ${errorMessage}`;
    } else if (error.request) {
      console.error('Cererea nu a primit răspuns:', error.request);
      message.value = 'Eroare de rețea: Serverul nu răspunde. Verifică consola (F12).';
    } else {
      console.error('Eroare de JavaScript (configurare):', error.message);
      message.value = `Eroare de aplicație: ${error.message}`;
    }
    isSuccess.value = false;
    // --- Sfârșit logică eroare ---

  } finally {
    console.log('--- PAS 6 (FINALLY): Se setează loading.value = false ---');
    loading.value = false;
  }
};
</script>

<style scoped>
/* Păstrăm stilurile tale și adăugăm câteva noi */
.create-post-container {
  border: 1px solid #ddd;
  padding: 25px;
  border-radius: 10px;
  background-color: #ffffff;
  margin-top: 25px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
h2 {
  color: #1c748c;
  margin-top: 0;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}
p {
  font-size: 0.9em;
  color: #555;
  line-height: 1.5;
}
.form-group {
  margin-bottom: 15px;
}
.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #444;
  font-size: 0.95em;
}
.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 6px;
  background-color: #fcfcfc;
  font-size: 1em;
}

/* STILURI NOI PENTRU DROPZONE */
.dropzone {
  border: 2px dashed #ccc;
  border-radius: 6px;
  padding: 25px;
  text-align: center;
  cursor: pointer;
  background-color: #fafafa;
  transition: background-color 0.2s, border-color 0.2s;
}
.dropzone:hover {
  background-color: #f5f5f5;
  border-color: #aaa;
}
.dropzone.drag-over {
  background-color: #e0f7fa; /* Albastru deschis când tragi un fișier deasupra */
  border-color: #1c748c;
}
.dropzone p {
  margin: 0;
  color: #777;
}

/* STILURI NOI PENTRU PREVIZUALIZĂRI */
.preview-label {
  font-size: 0.9em;
  color: #555;
  margin-top: 20px;
  margin-bottom: 10px;
}
.preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 6px;
}
.thumbnail {
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  background-color: #eee;
  cursor: move; /* Indică faptul că poate fi mutat */
}
.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Umple pătratul fără a distorsiona */
}
.remove-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  line-height: 18px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}
.remove-btn:hover {
  background-color: rgba(255, 0, 0, 0.8);
}
/* ... restul stilurilor tale ... */
.button-group {
  margin-top: 25px;
}
button {
  background-color: #1c748c;
  color: white;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
}
button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.success-message {
  color: #27ae60;
  margin-top: 15px;
  font-weight: bold;
}
.error-message {
  color: #c0392b;
  margin-top: 15px;
  font-weight: bold;
}
</style>